import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import type { RibbonControlId } from 'owa-ribbon-ids';
import { ActionControlId, MailRibbonControlId } from 'owa-ribbon-ids';
import * as ControlDef from './slrOptionsTabControlDefinitions';
import { getAccChecker } from 'owa-compose-actioncontrols/lib/components/controls/accChecker';
import { getAccCheckerState } from 'owa-mail-compose-ribbon/lib/utils/getAccCheckerState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { getProofingOptions } from 'owa-compose-actioncontrols/lib/components/controls/proofingOptions';
import { getShowMessageOptions } from 'owa-compose-actioncontrols/lib/components/controls/showMessageOptions';
import { showMessageOptionsDialog, switchBodyType } from 'owa-mail-compose-command';
import { getSwitchBodyType } from 'owa-compose-actioncontrols/lib/components/controls/switchBodyType';
import { proofingStore } from 'owa-editor-proofing-plugin/lib/store/store';
import setImportance from 'owa-mail-compose-actions/lib/actions/setImportance';
import { getImportance } from 'owa-compose-actioncontrols/lib/components/controls/setImportance';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';

/**
 * This function supplies a mapping from RibbonControlId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getSLROptionsTabControlDefinition = computedFn(
    (
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps | undefined => {
        const viewState: ComposeViewState | undefined =
            props.popoutComposeViewState ?? getComposeViewState();
        if (!viewState) {
            return undefined;
        }
        const isPopout = props.popoutComposeViewState != undefined;
        const editorId: string = viewState.editorId;

        switch (controlId) {
            case ActionControlId.Sensitivity:
                return ControlDef.getSensitivityControl(props);
            case MailRibbonControlId.Save:
                return ControlDef.getSaveControl(viewState);
            case MailRibbonControlId.Encrypt:
                return ControlDef.getEncryptControl(viewState, isPopout, props.targetWindow);
            case ActionControlId.CheckForAccessibilityIssues:
                return getAccChecker(viewState, getAccCheckerState(viewState)!, undefined);
            case ActionControlId.SwitchBodyType:
                const onSwitchBodyType = () => {
                    const targetWindow =
                        callEditorApi(viewState, 'getDocument')?.defaultView || window;
                    switchBodyType(viewState, targetWindow);
                };
                const isPlainText = viewState.bodyType != 'HTML';
                return getSwitchBodyType(editorId, isPlainText, onSwitchBodyType, undefined);
            case ActionControlId.ProofingOptions:
                const proofingDisabled =
                    viewState.proofing?.isProofingDisabled ||
                    !proofingStore.editorBxClients.has(viewState.proofing?.editorProofingId!);
                return getProofingOptions(viewState, proofingDisabled, undefined);
            case ActionControlId.ShowMessageOptions:
                const onMessageOptionDialog = () => {
                    if (viewState) {
                        const targetWindow =
                            callEditorApi(viewState, 'getDocument')?.defaultView || window;
                        showMessageOptionsDialog(viewState, targetWindow);
                    }
                };
                return getShowMessageOptions(editorId, onMessageOptionDialog, undefined);
            case ActionControlId.SetImportance:
                const onImportanceToggled = (viewState: ComposeViewState | undefined) => (
                    importance: ImportanceType
                ) => {
                    if (viewState) {
                        setImportance(
                            viewState,
                            viewState.importance === importance ? 'Normal' : importance
                        );
                    }
                };

                return getImportance(
                    editorId,
                    viewState.importance,
                    onImportanceToggled(viewState),
                    undefined
                );
            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getSLROptionsTabControlDefinition'
                );
        }
    }
);
